import { Subject, Observable } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Injectable, OnDestroy } from '@angular/core';

export enum SnViewport {
  Handset = 'handset',
  Tablet = 'tablet',
  Web = 'web',
}

export enum SnOrientation {
  Portrait = 'portrait',
  Landscape = 'landscape',
}

@Injectable({ providedIn: 'root' })
export class SnScreenService implements OnDestroy {
  private _viewport: SnViewport;
  private readonly _viewportChange$ = new Subject<SnViewport>();

  private _orientation: SnOrientation;
  private readonly _orientationChange$ = new Subject<SnOrientation>();

  private readonly _destroy$ = new Subject<void>();

  constructor(private readonly _breakpointObserver: BreakpointObserver) {
    this._updateViewport();
    this._initViewportSubscriptions();

    this._updateOrientation();
    this._initOrientationSubscriptions();
  }

  get viewport(): SnViewport {
    return this._viewport;
  }

  get viewportChange$(): Observable<SnViewport> {
    return this._viewportChange$.asObservable();
  }

  get orientation(): SnOrientation {
    return this._orientation;
  }

  get orientationChange$(): Observable<SnOrientation> {
    return this._orientationChange$.asObservable();
  }

  public ngOnDestroy(): void {
    this._viewportChange$.complete();
    this._orientationChange$.complete();

    this._destroy$.next();
    this._destroy$.complete();
  }

  private _initViewportSubscriptions(): void {
    this._breakpointObserver
      .observe([Breakpoints.Handset, Breakpoints.Tablet, Breakpoints.Web])
      .pipe(
        filter((state) => state.matches),
        takeUntil(this._destroy$),
      )
      .subscribe(() => this._updateViewport());
  }

  private _updateViewport(): void {
    if (this._breakpointObserver.isMatched(Breakpoints.Web)) {
      this._setViewport(SnViewport.Web);
    } else if (this._breakpointObserver.isMatched(Breakpoints.Tablet)) {
      this._setViewport(SnViewport.Tablet);
    } else {
      this._setViewport(SnViewport.Handset);
    }
  }

  private _setViewport(viewport: SnViewport): void {
    if (this._viewport !== viewport) {
      this._viewport = viewport;
      this._viewportChange$.next(viewport);
    }
  }

  private _initOrientationSubscriptions(): void {
    this._breakpointObserver
      .observe(['(orientation: portrait)', '(orientation: landscape)'])
      .pipe(
        filter((state) => state.matches),
        takeUntil(this._destroy$),
      )
      .subscribe(() => this._updateOrientation());
  }

  private _updateOrientation(): void {
    if (this._breakpointObserver.isMatched('(orientation: landscape)')) {
      this._setOrientation(SnOrientation.Landscape);
    } else {
      this._setOrientation(SnOrientation.Portrait);
    }
  }

  private _setOrientation(orientation: SnOrientation): void {
    if (this._orientation !== orientation) {
      this._orientation = orientation;
      this._orientationChange$.next(orientation);
    }
  }
}
